import { DEFAULT_CAMERA_POSITIONS, ANIMATION_TIMINGS, getDefaultCameraPosition } from './camera/CameraConfig.js'
import {
  getLaunchPlatformCameraPosition,
  getBlackArrowCameraPosition,
  getSceneHotspots,
  easeInOutCubic
} from './camera/CameraService.js'
import react, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useControls } from 'leva'
import { useAnimation } from './animations.jsx'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { getContentForPOI, DIMENSION_MEASUREMENTS, SCENE2_DEFAULT_CONTENT } from './content/TextContent.jsx'

// Helper function to get label text for POI names
function getPOILabel(poiName, isLandscape = true) {
  const landscapeLabels = {
    // Scene 2 POIs (Black Arrow stages)
    'POI_BA_ST2': 'Second Stage',
    'POI_BA_ST1': 'First Stage',
    'POI_BA_ST3': 'Third Stage',
    'POI_BA_PRO': 'Prospero',
    // Scene 1 POIs (Launch Platform)
    'POI_LP_Clamp': 'Propulsion/Engine',
    'POI_LP_Fuel': 'Potential Revival',
    'POI_LP_Shack': 'Launch History',
    'POI_LP_Origin': 'Black Arrow',
    // Scene 2 Origin POI
    'POI_ORIGIN': 'Black Arrow'
  }
  
  const portraitLabels = {
    // Scene 2 POIs (Black Arrow stages)
    'POI_BA_ST2': 'Second Stage',
    'POI_BA_ST1': 'First Stage',
    'POI_BA_ST3': 'Third Stage',
    'POI_BA_PRO': 'Prospero',
    // Scene 1 POIs (Launch Platform)
    'POI_LP_Clamp': 'Propulsion',
    'POI_LP_Fuel': 'Revival',
    'POI_LP_Shack': 'History',
    'POI_LP_Origin': 'Black Arrow',
    // Scene 2 Origin POI
    'POI_ORIGIN': 'Black Arrow'
  }
  
  const labelMap = isLandscape ? landscapeLabels : portraitLabels
  return labelMap[poiName] || poiName
}


export function MoveButton({ isAtPOI = false, isPortrait = false }) {
  const { Stage_1_Offset, setStage_1_Offset, Stage_2_Offset, setStage_2_Offset, Stage_3_Offset, setStage_3_Offset, leftFairingRotation, setLeftFairingRotation, rightFairingRotation, setRightFairingRotation, hdriBlend, setHdriBlend } = useAnimation()
  const [isAnimating, setIsAnimating] = useState(false)
  const [isReversed, setIsReversed] = useState(false)
  const triggerLaunchRef = useRef(null)
  const [windowDimensions, setWindowDimensions] = useState({ width: window.innerWidth, height: window.innerHeight })
  const lastOrientationRef = useRef(window.innerHeight > window.innerWidth)
  const scene2SetupDoneRef = useRef(false)
  const animationOrientationRef = useRef(null)  // Track which orientation the current animation started in
  const activeAnimationFramesRef = useRef(new Set())  // Track all active animation frames
  const activeTimeoutsRef = useRef(new Set())  // Track all pending timeouts
  const orientationChangeHandledRef = useRef(false)  // Track when we just handled an orientation change

  // Helper to track and cancel animation frames
  const trackAnimationFrame = useCallback((frameId) => {
    activeAnimationFramesRef.current.add(frameId)
    return frameId
  }, [])

  const trackTimeout = useCallback((timeoutId) => {
    activeTimeoutsRef.current.add(timeoutId)
    return timeoutId
  }, [])

  const cancelAllAnimationFrames = useCallback(() => {
    activeAnimationFramesRef.current.forEach(frameId => {
      cancelAnimationFrame(frameId)
    })
    activeAnimationFramesRef.current.clear()
  }, [])

  const cancelAllTimeouts = useCallback(() => {
    activeTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId)
    })
    activeTimeoutsRef.current.clear()
  }, [])

  // Apply Scene 2 orientation-specific setup (rotation + camera offset)
  const applyScene2Setup = useCallback(() => {
    if (scene2SetupDoneRef.current) return
    scene2SetupDoneRef.current = true
    
    // Apply initial Scene 2 camera positioning with orientation offset
    const isPortrait = window.innerWidth < window.innerHeight
    const isLandscape = !isPortrait
    const isMobile = window.innerWidth <= 767
    const isMobileLandscape = isMobile && isLandscape
    const defaultPos = getDefaultCameraPosition('scene2', isLandscape)
    const basePos = defaultPos.position.clone()
    const baseTarget = defaultPos.target.clone()
    
    // Apply x offset for better composition (portrait and desktop landscape, but not mobile landscape)
    if (!isMobileLandscape) {
      baseTarget.x = 2
    }

    
    window.dispatchEvent(new CustomEvent('scene-camera-move', {
      detail: {
        targetPosition: basePos,
        targetLookAt: baseTarget,
        duration: ANIMATION_TIMINGS.cameraMovement
      }
    }))
  }, [])

  // Apply Scene 2 setup without the guard (for mid-animation orientation changes)
  const applyScene2SetupForced = useCallback(() => {
    
    // Apply orientation-specific camera positioning for Scene 2
    const isPortrait = window.innerWidth < window.innerHeight
    const isLandscape = !isPortrait
    const isMobile = window.innerWidth <= 767
    const isMobileLandscape = isMobile && isLandscape
    const defaultPos = getDefaultCameraPosition('scene2', isLandscape)
    const basePos = defaultPos.position.clone()
    const baseTarget = defaultPos.target.clone()
    
    // Apply x offset for better composition (portrait and desktop landscape, but not mobile landscape)
    if (!isMobileLandscape) {
      baseTarget.x = 2
    }

    
    // Dispatch a snap event to stop current animation and snap to default position instantly
    window.dispatchEvent(new CustomEvent('scene-camera-snap', {
      detail: {
        targetPosition: basePos,
        targetLookAt: baseTarget
      }
    }))
    
  }, [])

  const animateAll = useCallback((duration = 6000, reverse = false) => {
    const hdriBlendDuration = 2000
    const stage1Duration = duration / 4
    const fairingDuration = duration / 4
    const stage2Duration = duration / 4
    const stage3Duration = duration / 4

    const totalDuration = reverse 
      ? 1000 + stage3Duration + hdriBlendDuration
      : hdriBlendDuration + 750 + stage3Duration

    if (!reverse) {
      animateHdriBlend(hdriBlendDuration)
      trackTimeout(setTimeout(() => {
        // Start all stage animations immediately after HDRI blend
        animateStage1()
        trackTimeout(setTimeout(animateFairingRotation, 250))
        trackTimeout(setTimeout(animateStage2, 500))
        trackTimeout(setTimeout(animateStage3, 750))
      }, hdriBlendDuration))
    } else {
      trackTimeout(setTimeout(animateStage3, 0))
      trackTimeout(setTimeout(animateStage2, 250))
      trackTimeout(setTimeout(animateStage1, 500))
      trackTimeout(setTimeout(animateFairingRotation, 750))
      trackTimeout(setTimeout(() => animateHdriBlend(hdriBlendDuration), 1000 + stage3Duration))
    }

    return totalDuration

    function animateStage1() {
      const startStage1 = Stage_1_Offset
      const targetStage1 = reverse ? 0 : 2
      const changeStage1 = targetStage1 - startStage1
      const startTimeStage1 = performance.now()

      function step(now) {
        const elapsed = now - startTimeStage1
        const eased = easeInOutCubic(Math.min(elapsed / stage1Duration, 1))
        setStage_1_Offset(startStage1 + changeStage1 * eased)
        
        if (elapsed < stage1Duration) {
          const frameId = requestAnimationFrame(step)
          trackAnimationFrame(frameId)
        }
      }
      const frameId = requestAnimationFrame(step)
      trackAnimationFrame(frameId)
    }

    function animateFairingRotation() {
      const startLeftFairing = leftFairingRotation[0]
      const startRightFairing = rightFairingRotation[0]
      const targetLeft = reverse ? 0 : 20 * Math.PI / 180
      const targetRight = reverse ? 0 : -20 * Math.PI / 180
      const changeLeftFairing = targetLeft - startLeftFairing
      const changeRightFairing = targetRight - startRightFairing
      const startTimeFairing = performance.now()

      function step(now) {
        const elapsed = now - startTimeFairing
        const eased = easeInOutCubic(Math.min(elapsed / fairingDuration, 1))
        setLeftFairingRotation([startLeftFairing + changeLeftFairing * eased, 0, 0])
        setRightFairingRotation([startRightFairing + changeRightFairing * eased, 0, 0])
        
        if (elapsed < fairingDuration) {
          const frameId = requestAnimationFrame(step)
          trackAnimationFrame(frameId)
        }
      }
      const frameId = requestAnimationFrame(step)
      trackAnimationFrame(frameId)
    }

    function animateStage2() {
      const startStage2 = Stage_2_Offset
      const targetStage2 = reverse ? 0 : 4
      const changeStage2 = targetStage2 - startStage2
      const startTimeStage2 = performance.now()

      function step(now) {
        const elapsed = now - startTimeStage2
        const eased = easeInOutCubic(Math.min(elapsed / stage2Duration, 1))
        setStage_2_Offset(startStage2 + changeStage2 * eased)
        
        if (elapsed < stage2Duration) {
          const frameId = requestAnimationFrame(step)
          trackAnimationFrame(frameId)
        }
      }
      const frameId = requestAnimationFrame(step)
      trackAnimationFrame(frameId)
    }

    function animateStage3() {
      const startStage3 = Stage_3_Offset
      const targetStage3 = reverse ? 0 : 1.5
      const changeStage3 = targetStage3 - startStage3
      const startTimeStage3 = performance.now()

      function step(now) {
        const elapsed = now - startTimeStage3
        const eased = easeInOutCubic(Math.min(elapsed / stage3Duration, 1))
        setStage_3_Offset(startStage3 + changeStage3 * eased)
        
        if (elapsed < stage3Duration) {
          const frameId = requestAnimationFrame(step)
          trackAnimationFrame(frameId)
        }
      }
      const frameId = requestAnimationFrame(step)
      trackAnimationFrame(frameId)
    }

    function animateHdriBlend(blendDuration) {
      const startBlend = hdriBlend
      const targetBlend = reverse ? 0 : 1
      const changeBlend = targetBlend - startBlend
      const startTimeBlend = performance.now()

      function step(now) {
        const elapsed = now - startTimeBlend
        const eased = easeInOutCubic(Math.min(elapsed / blendDuration, 1))
        const newBlend = startBlend + changeBlend * eased
        setHdriBlend(newBlend)
        
        // When blend reaches 1.0 (entering Scene 2), apply orientation setup
        if (!reverse && newBlend >= 1) {
          applyScene2Setup()
        }
        
        if (elapsed < blendDuration) {
          const frameId = requestAnimationFrame(step)
          trackAnimationFrame(frameId)
        }
      }
      const frameId = requestAnimationFrame(step)
      trackAnimationFrame(frameId)
    }
  }, [Stage_1_Offset, Stage_2_Offset, Stage_3_Offset, leftFairingRotation, rightFairingRotation, hdriBlend, 
      setStage_1_Offset, setStage_2_Offset, setStage_3_Offset, setLeftFairingRotation, setRightFairingRotation, setHdriBlend,
      trackAnimationFrame, trackTimeout, applyScene2Setup])

  const handleClick = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true)
      scene2SetupDoneRef.current = false  // Reset for next animation
      
      // Store the orientation that this animation started in
      const startOrientation = window.innerHeight > window.innerWidth
      animationOrientationRef.current = startOrientation

      // Dispatch animation-start event IMMEDIATELY when button is clicked
      // This allows camera rotation to start right away
      window.dispatchEvent(new CustomEvent('animation-start', {
        detail: {
          isReversed: isReversed
        }
      }))

      // Move camera to appropriate starting position
      if (!isReversed) {
        // Going to Scene 2: move to Scene 2 defaults with orientation offset
        const isPortrait = window.innerWidth < window.innerHeight
        const isLandscape = !isPortrait
        const isMobile = window.innerWidth <= 767
        const isMobileLandscape = isMobile && isLandscape
        const defaultScene2 = getDefaultCameraPosition('scene2', isLandscape)
        const targetWithOffset = defaultScene2.target.clone()
        
        // Apply offset for portrait and desktop landscape, but not mobile landscape
        if (!isMobileLandscape) {
          targetWithOffset.x = 2
        }

        
        window.dispatchEvent(new CustomEvent('scene-camera-move', {
          detail: {
            targetPosition: defaultScene2.position.clone(),
            targetLookAt: targetWithOffset,
            duration: ANIMATION_TIMINGS.cameraMovement
          }
        }))
      } else {
        // Returning to Scene 1: move to Scene 1 position directly
        const isPortrait = window.innerWidth < window.innerHeight
        const isLandscape = !isPortrait
        const defaultScene1 = getDefaultCameraPosition('scene1', isLandscape)
        window.dispatchEvent(new CustomEvent('scene-camera-move', {
          detail: {
            targetPosition: defaultScene1.position.clone(),
            targetLookAt: defaultScene1.target.clone(),
            duration: ANIMATION_TIMINGS.cameraMovement
          }
        }))
      }

      const totalDuration = animateAll(6000, isReversed)
      setTimeout(() => {
        setIsAnimating(false)
        setIsReversed(!isReversed)
        animationOrientationRef.current = null  // Clear when animation completes
      }, totalDuration)
    }
  }, [isAnimating, isReversed, animateAll])

  // Listen for trigger-launch event from origin POI
  useEffect(() => {
    const handleTriggerLaunch = () => {
      handleClick()
    }
    
    window.addEventListener('trigger-launch', handleTriggerLaunch)
    return () => {
      window.removeEventListener('trigger-launch', handleTriggerLaunch)
    }
  }, [handleClick])

  // Track window dimensions for orientation detection
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  // Separate handler for mid-animation orientation changes
  useEffect(() => {
    const handleOrientationChangeImmediate = () => {
      
      // Use a small delay to allow browser to update window dimensions
      setTimeout(() => {
        const isPortraitNow = window.innerHeight > window.innerWidth
        const isLandscapeNow = !isPortraitNow
        
        
  // If orientation changed DURING active Scene 2 animation (and we're entering Scene 2), handle it immediately
  // Do NOT handle mid-animation orientation changes when we're reversing (returning to Scene 1)
  if (isAnimating && !isReversed && hdriBlend >= 1 && animationOrientationRef.current !== null && isPortraitNow !== animationOrientationRef.current) {
          
          // Flag that we're handling an orientation change to prevent other effects from overriding
          orientationChangeHandledRef.current = true
          
          // Cancel all ongoing animations AND pending timeouts
          cancelAllAnimationFrames()
          cancelAllTimeouts()
          
          // Skip to final state: all stages fully extended
          setStage_1_Offset(2)
          setStage_2_Offset(4)
          setStage_3_Offset(1.5)
          setLeftFairingRotation([20 * Math.PI / 180, 0, 0])
          setRightFairingRotation([-20 * Math.PI / 180, 0, 0])
          
          // Update animation orientation tracker and apply the new orientation's camera offset and rocket rotation
          animationOrientationRef.current = isPortraitNow
          applyScene2SetupForced()
          
          // Clear the flag after a brief delay so other effects can run normally
          setTimeout(() => {
            orientationChangeHandledRef.current = false
          }, 100)
        } else if (isAnimating && isReversed) {
          // Orientation changed DURING reverse animation (returning to Scene 1)
          // Handle at any point during the reverse transition, not just when hdriBlend < 1
          orientationChangeHandledRef.current = true
          
          // Cancel ongoing camera animation
          window.__forceAnimationComplete = true
          
          // Get Scene 1 default camera position for the new orientation
          const scene1Defaults = getDefaultCameraPosition('scene1', isLandscapeNow)
          
          // Snap camera to correct position
          window.dispatchEvent(new CustomEvent('scene-camera-snap', {
            detail: {
              targetPosition: scene1Defaults.position,
              targetLookAt: scene1Defaults.target
            }
          }))
          
          setTimeout(() => {
            orientationChangeHandledRef.current = false
          }, 100)
        } else if (!isReversed && hdriBlend >= 1 && !isAnimating && isPortraitNow !== lastOrientationRef.current) {
          // Orientation changed AFTER animation completed (in Scene 2 but not animating)
          lastOrientationRef.current = isPortraitNow
          orientationChangeHandledRef.current = true
          applyScene2SetupForced()
          
          setTimeout(() => {
            orientationChangeHandledRef.current = false
          }, 100)
        }
      }, 50)  // Wait 50ms for browser to update dimensions
    }
    
    window.addEventListener('orientationchange', handleOrientationChangeImmediate)
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChangeImmediate)
    }
  }, [isAnimating, isReversed, hdriBlend, applyScene2SetupForced, cancelAllAnimationFrames, cancelAllTimeouts])

  // Show reset button only in Scene 2 (hdriBlend >= 1), hide in Scene 1
  if (hdriBlend < 1 || isAnimating || isAtPOI) return null

  return (
    <div className="move-button-container">
      <button
        className="DSL_Rectangular_Button"
        onClick={handleClick}
      >
        BACK TO PROGRAMME OVERVIEW
      </button>
    </div>
  );
}

// Helper component to get camera and size from within Canvas
function CameraDataProvider({ onCameraData }) {
  const { camera, size } = useThree()
  
  useEffect(() => {
    onCameraData({ camera, size })
  }, [camera, size, onCameraData])
  
  return null
}

// Hotspots component - tracks POI groups and dispatches screen positions
export function Hotspots({ scenesRef, cameraRef }) {
  const { size } = useThree()
  const { hdriBlend } = useAnimation()
  
  useFrame(() => {
    // Show hotspots when Scene 2 is fully loaded
    const animationComplete = hdriBlend >= 1
    
    if (hdriBlend >= 1 && !animationComplete) {
      window.__hotspotsFrame = []
      return
    }
    
    if (!scenesRef || !cameraRef?.current) return
    
    // Use centralized hotspot calculation
    const hotspots = getSceneHotspots(scenesRef, hdriBlend, cameraRef.current, size)
    window.__hotspotsFrame = hotspots
  })
  
  return null
}

// Map of POI names to their desired dimension line heights (in world units)
// These will be projected to screen space and scaled consistently
const lineHeightMapWorldUnits = {
  'ST1_Body': 0.475,       // Stage 1 - world units
  'ST2_Body': 0.325,       // Stage 2 - world units
  'ThirdStage': 0.08,     // Third Stage - world units
  'Prospero': 0.1        // Prospero - world units
}

// Helper to get dimension line height for a model (in world units)
function getModelBoundsY(scenesRef, modelName) {
  return lineHeightMapWorldUnits[modelName] || 0.3 // Default fallback in world units
}

// DimensionLinesCanvas - renders dimension lines and connectors using Canvas 2D API
function DimensionLinesCanvas({ hotspots, isAtPOI, hdriBlend, scenesRef, cameraZoom = 2000, cameraRotation = 0, portraitMode = false, isMobile = false }) {
  const canvasRef = useRef(null)
  const animationFrameIdRef = useRef(null)
  const hotspotsRef = useRef(hotspots)
  const isAtPOIRef = useRef(isAtPOI)
  const hdriBlendRef = useRef(hdriBlend)
  const animationProgressRef = useRef(0)
  const animationStartTimeRef = useRef(null)
  const animationDurationRef = useRef(600) // 600ms animation
  const prevSeparationCompleteRef = useRef(false)
  const shouldDrawLinesRef = useRef(false) // Flag to control when lines should be visible
  const isReversingRef = useRef(false) // Track if we're in reverse animation
  const { Stage_1_Offset, Stage_2_Offset, Stage_3_Offset, earthRotation: contextEarthRotation, setEarthRotation } = useAnimation()
  
  // Dimension line configuration with Leva controls
  const dimensionConfig = useControls('Dimension Lines', {
    offsetX: { value: -0.12, min: -0.5, max: 0.5, step: 0.001 },
    offsetY: { value: 0.28, min: -0.5, max: 0.5, step: 0.001 },
    diameter: { value: 0.15, min: 0.05, max: 0.5, step: 0.001 }
  })
  
  // Earth HDRI rotation controls
  const earthRotationControls = useControls('Earth HDRI', {
    rotationX: { value: -0.3, min: -Math.PI, max: Math.PI, step: 0.01, label: 'Rotation X' },
    rotationY: { value: 0.0, min: -Math.PI, max: Math.PI, step: 0.01, label: 'Rotation Y' },
    rotationZ: { value: 1.57, min: -Math.PI, max: Math.PI, step: 0.01, label: 'Rotation Z' }
  })
  
  // Sync Leva controls with animation context
  useEffect(() => {
    setEarthRotation([earthRotationControls.rotationX, earthRotationControls.rotationY, earthRotationControls.rotationZ])
  }, [earthRotationControls.rotationX, earthRotationControls.rotationY, earthRotationControls.rotationZ, setEarthRotation])
  
  const poiBoundsMap = useMemo(() => ({
    'POI_BA_ST2': 'ST2_Body',
    'POI_BA_ST1': 'ST1_Body',
    'POI_BA_ST3': 'ThirdStage',
    'POI_BA_PRO': 'Prospero'
  }), [])

  const prevIsAtPOIRef = useRef(false)
  
  // Function to convert world units to screen pixels based on viewport
  // For orthographic camera with zoom that varies by orientation: pixels = worldUnits * (2000 / zoom) * (canvasHeight / 2 / 1000)
  // This keeps line lengths consistent: higher zoom (landscape=2000) → same size, lower zoom (portrait=400) → scales to match
  // We use 2000 as the reference zoom value (landscape default)
  const worldUnitsToScreenPixels = useCallback((worldUnits) => {
    if (!canvasRef.current) return 100 // Fallback
    const REFERENCE_ZOOM = 2000 // Landscape zoom
    const canvasHeight = canvasRef.current.height
    const zoomScale = REFERENCE_ZOOM / cameraZoom // Compensate for zoom differences
    const pixelsPerWorldUnit = (2000 * (canvasHeight / 2 / 1000)) / zoomScale
    return worldUnits * pixelsPerWorldUnit
  }, [cameraZoom])

  // Keep refs in sync with props and check for animation trigger
  useEffect(() => {
    // Always keep hotspots in sync
    hotspotsRef.current = hotspots
    
    // Check if rocket separation is complete
    const separationComplete = Stage_1_Offset >= 1.9 && Stage_2_Offset >= 3.9 && Stage_3_Offset >= 1.4
    
    // Trigger animation when separation state changes
    if (prevSeparationCompleteRef.current !== separationComplete) {
      if (separationComplete) {
        // Forward animation: rocket separated
        animationStartTimeRef.current = performance.now()
        shouldDrawLinesRef.current = true
        isReversingRef.current = false
      } else {
        // Reverse animation: resetting/collapsing - hide lines immediately
        shouldDrawLinesRef.current = false
        animationProgressRef.current = 0
        isReversingRef.current = false
        animationStartTimeRef.current = null
      }
    }
    prevSeparationCompleteRef.current = separationComplete
    
  }, [Stage_1_Offset, Stage_2_Offset, Stage_3_Offset, hotspots])

  useEffect(() => {
    isAtPOIRef.current = isAtPOI
    // Trigger animation when returning to default view from POI zoom in Scene 2
    if (!isAtPOI && prevIsAtPOIRef.current && hdriBlend >= 1) {
      animationProgressRef.current = 0 // Reset to start of animation
      animationStartTimeRef.current = performance.now()
      shouldDrawLinesRef.current = true
      isReversingRef.current = false
    }
    prevIsAtPOIRef.current = isAtPOI
  }, [isAtPOI, hdriBlend])

  useEffect(() => {
    hdriBlendRef.current = hdriBlend
    
    // Reset animation state when leaving scene 2 (going back to scene 1)
    // This ensures labels will fade in properly on next transition to scene 2
    if (hdriBlend < 1) {
      shouldDrawLinesRef.current = false
      animationProgressRef.current = 0
      isReversingRef.current = false
      animationStartTimeRef.current = null
    }
  }, [hdriBlend])

  // Set initial canvas size
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.width = window.innerWidth
      canvasRef.current.height = window.innerHeight
    }

    const handleResize = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth
        canvasRef.current.height = window.innerHeight
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Use requestAnimationFrame for continuous updates
  useEffect(() => {
    const draw = () => {
      // Always schedule next frame
      animationFrameIdRef.current = requestAnimationFrame(draw)

      // Dispatch animation progress for labels even when not drawing lines
      // This ensures labels get the correct opacity state at all times
      window.dispatchEvent(new CustomEvent('dimension-animation-progress', {
        detail: {
          progress: animationProgressRef.current,
          shouldDraw: shouldDrawLinesRef.current
        }
      }))

      // Check conditions - only draw if we should show lines
      // Hide dimension lines on mobile devices (both portrait and landscape)
      if (!canvasRef.current || hotspotsRef.current.length === 0 || isAtPOIRef.current || hdriBlendRef.current < 1 || !shouldDrawLinesRef.current || isMobile) {
        const canvas = canvasRef.current
        if (canvas) {
          const ctx = canvas.getContext('2d')
          ctx.clearRect(0, 0, canvas.width, canvas.height)
        }
        return
      }

      // Calculate animation progress
      const now = performance.now()
      if (animationStartTimeRef.current === null) {
        animationStartTimeRef.current = now
      }
      const elapsed = now - animationStartTimeRef.current
      let progress = Math.min(elapsed / animationDurationRef.current, 1)
      
      // Reverse the progress if we're in reverse animation
      if (isReversingRef.current) {
        progress = 1 - progress
        // When reverse animation completes, hide the lines
        if (progress <= 0) {
          shouldDrawLinesRef.current = false
        }
      }
      
      animationProgressRef.current = progress

      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw lines for each visible POI (excluding origin POI)
      hotspotsRef.current
        .filter(h => h.name.startsWith('POI_BA_') && h.name !== 'POI_ORIGIN' && h.isVisible)
        .forEach(hotspot => {
          const modelName = poiBoundsMap[hotspot.name]
          const lineHeightWorldUnits = modelName ? getModelBoundsY(scenesRef, modelName) : 1.2
          const lineHeight = worldUnitsToScreenPixels(lineHeightWorldUnits)
          const halfHeight = lineHeight / 2

          // Detect tablet portrait for dimension line offset
          const isTabletPortrait = !isMobile && portraitMode && window.innerWidth > 767 && window.innerWidth <= 1024
          const tabletPortraitOffsetX = isTabletPortrait ? 20 : 0

          // Set line style
          ctx.strokeStyle = 'white'
          ctx.lineWidth = 2
          ctx.globalAlpha = 0.9

          // Save the current context state
          ctx.save()

          // Translate to hotspot position with tablet portrait offset
          ctx.translate(hotspot.x + tabletPortraitOffsetX, hotspot.y)

          // Rotate with camera rotation
          ctx.rotate(cameraRotation)

          // Scale based on animation progress (easing function: easeOutQuad)
          const easeOutQuad = 1 - Math.pow(1 - animationProgressRef.current, 2)
          ctx.scale(easeOutQuad, easeOutQuad)

          // Draw main vertical line (centered at origin now)
          ctx.beginPath()
          ctx.moveTo(0, -halfHeight)
          ctx.lineTo(0, halfHeight)
          ctx.stroke()

          // Draw top end cap
          ctx.beginPath()
          ctx.moveTo(-5, -halfHeight)
          ctx.lineTo(5, -halfHeight)
          ctx.stroke()

          // Draw bottom end cap
          ctx.beginPath()
          ctx.moveTo(-5, halfHeight)
          ctx.lineTo(5, halfHeight)
          ctx.stroke()

          // Draw measurement text in the center
          // Counter-rotate the text to keep it upright
          ctx.rotate(-cameraRotation)
          
          ctx.fillStyle = 'white'
          // Use smaller font on mobile
          const fontSize = isMobile ? 10 : 14
          ctx.font = `${fontSize}px CGAthin, Arial, sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.globalAlpha = 1.0 * easeOutQuad
          
          // Get measurement value from TextContent based on POI name
          let measurementValue = lineHeightWorldUnits
          switch(hotspot.name) {
            case 'POI_BA_ST1':
              measurementValue = DIMENSION_MEASUREMENTS.FIRST_STAGE_HEIGHT
              break
            case 'POI_BA_ST2':
              measurementValue = DIMENSION_MEASUREMENTS.SECOND_STAGE_HEIGHT
              break
            case 'POI_BA_ST3':
              measurementValue = DIMENSION_MEASUREMENTS.THIRD_STAGE_HEIGHT
              break
            case 'POI_BA_PRO':
              measurementValue = DIMENSION_MEASUREMENTS.PROSPERO_HEIGHT
              break
          }
          
          // Format the measurement
          const measurementText = `${measurementValue.toFixed(2)}m`
          
          // Draw text with background for better readability
          const textMetrics = ctx.measureText(measurementText)
          const textWidth = textMetrics.width
          const textHeight = 16
          const padding = 4
          
          // Draw solid black background rectangle
          ctx.fillStyle = 'rgb(0, 0, 0)'
          ctx.fillRect(-textWidth/2 - padding, -textHeight/2 - padding, textWidth + padding*2, textHeight + padding*2)
          
          // Draw text
          ctx.fillStyle = 'white'
          ctx.fillText(measurementText, 0, 0)

          // Restore the context state
          ctx.restore()
        })

      // Draw horizontal diameter line for Stage 1 (First Stage)
      const stage1Hotspot = hotspotsRef.current.find(h => h.name === 'POI_BA_ST1' && h.isVisible)
      if (stage1Hotspot) {
        const diameterWidth = worldUnitsToScreenPixels(dimensionConfig.diameter)
        const halfWidth = diameterWidth / 2
        
        // Detect tablet portrait for dimension line offset
        const isTabletPortrait = !isMobile && portraitMode && window.innerWidth > 767 && window.innerWidth <= 1024
        const tabletPortraitOffsetX = isTabletPortrait ? 5 : 0
        
        // Use configurable offsets (convert to screen pixels)
        const offsetX = worldUnitsToScreenPixels(dimensionConfig.offsetX)
        const offsetY = worldUnitsToScreenPixels(dimensionConfig.offsetY)
        
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 2
        ctx.globalAlpha = 0.9
        
        ctx.save()
        
        // Translate to hotspot position with tablet portrait offset
        ctx.translate(stage1Hotspot.x + tabletPortraitOffsetX, stage1Hotspot.y)
        
        // Rotate with camera rotation
        ctx.rotate(cameraRotation)
        
        // Apply offsets in the rotated coordinate system
        ctx.translate(offsetX, offsetY)
        
        // Scale based on animation progress
        const easeOutQuad = 1 - Math.pow(1 - animationProgressRef.current, 2)
        ctx.scale(easeOutQuad, easeOutQuad)
        
        // Draw main horizontal line
        ctx.beginPath()
        ctx.moveTo(-halfWidth, 0)
        ctx.lineTo(halfWidth, 0)
        ctx.stroke()
        
        // Draw left end cap
        ctx.beginPath()
        ctx.moveTo(-halfWidth, -5)
        ctx.lineTo(-halfWidth, 5)
        ctx.stroke()
        
        // Draw right end cap
        ctx.beginPath()
        ctx.moveTo(halfWidth, -5)
        ctx.lineTo(halfWidth, 5)
        ctx.stroke()
        
        // Draw measurement text in the center
        // Counter-rotate the text to keep it upright
        ctx.rotate(-cameraRotation)
        
        ctx.fillStyle = 'white'
        // Use smaller font on mobile
        const fontSize = isMobile ? 10 : 14
        ctx.font = `${fontSize}px CGAthin, Arial, sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.globalAlpha = 1.0 * easeOutQuad
        
        // Format the measurement (use First Stage diameter from TextContent)
        const measurementText = `${DIMENSION_MEASUREMENTS.FIRST_STAGE_DIAMETER.toFixed(2)}m`
        
        // Draw text with background for better readability
        const textMetrics = ctx.measureText(measurementText)
        const textWidth = textMetrics.width
        const textHeight = 16
        const padding = 4
        
        // Draw solid black background rectangle
        ctx.fillStyle = 'rgb(0, 0, 0)'
        ctx.fillRect(-textWidth/2 - padding, -textHeight/2 - padding, textWidth + padding*2, textHeight + padding*2)
        
        // Draw text
        ctx.fillStyle = 'white'
        ctx.fillText(measurementText, 0, 0)
        
        ctx.restore()
      }
    }

    animationFrameIdRef.current = requestAnimationFrame(draw)

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current)
      }
    }
  }, [scenesRef, cameraRotation, portraitMode, worldUnitsToScreenPixels, poiBoundsMap, dimensionConfig, isMobile])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 99
      }}
    />
  )
}

// HotspotsOverlay - DOM overlay component to be rendered outside Canvas
export function HotspotsOverlay({ isAtPOI, onPOIStateChange, scenesRef, cameraZoom = 2000, cameraRotation = 0, onBackToDefaultReady }) {
  const [hotspots, setHotspots] = useState([])
  const [showHotspots, setShowHotspots] = useState(false)
  const [labelOpacity, setLabelOpacity] = useState(0)
  const [scene1LabelOpacity, setScene1LabelOpacity] = useState(1)
  const [currentPOI, setCurrentPOI] = useState(null)
  const fadeTimeoutRef = useRef(null)
  const hasShownRef = useRef(false)
  const { hdriBlend, earthEnvBlend, setEarthEnvBlend, scene3Blend, setScene3Blend } = useAnimation()
  const [portraitMode, setPortraitMode] = useState(window.innerHeight > window.innerWidth)
  const lastHotspotsRef = useRef(null)
  const lastHdriBlendRef = useRef(null)
  
  // Generate random rotation for each POI label (sticker effect)
  const labelRotations = useMemo(() => {
    const rotations = {}
    const poiNames = ['POI_LP_Clamp', 'POI_LP_Fuel', 'POI_LP_Shack', 'POI_LP_Origin', 
                      'POI_BA_ST1', 'POI_BA_ST2', 'POI_BA_ST3', 'POI_BA_PRO']
    poiNames.forEach(name => {
      // Random rotation between -3 and +3 degrees
      rotations[name] = (Math.random() * 6) - 3
    })
    return rotations
  }, [])
  
  // Listen for orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      setPortraitMode(window.innerHeight > window.innerWidth)
    }
    
    window.addEventListener('orientationchange', handleOrientationChange)
    window.addEventListener('resize', handleOrientationChange)
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
      window.removeEventListener('resize', handleOrientationChange)
    }
  }, [])
  
  // Handle orientation changes when viewing a POI (especially Prospero in scene 3)
  useEffect(() => {
    const handlePOIOrientationChange = () => {
      // Only update if we're currently viewing a POI in scene 2 or 3
      if (currentPOI && hdriBlend >= 1) {
        setTimeout(() => {
          const isLandscape = window.innerWidth > window.innerHeight
          
          // Get the updated camera position for the current POI with new orientation
          let cameraConfig = null
          if (currentPOI.startsWith('POI_BA_')) {
            cameraConfig = getBlackArrowCameraPosition(currentPOI, isLandscape)
          } else if (currentPOI.startsWith('POI_LP_')) {
            cameraConfig = getLaunchPlatformCameraPosition(currentPOI, isLandscape)
          }
          
          if (cameraConfig) {
            window.dispatchEvent(new CustomEvent('scene-camera-move', {
              detail: {
                targetPosition: cameraConfig.position,
                targetLookAt: cameraConfig.target,
                duration: ANIMATION_TIMINGS.orientationChange // 0ms for instant update
              }
            }))
          }
        }, 100) // Small delay to ensure window dimensions are updated
      }
    }
    
    window.addEventListener('orientationchange', handlePOIOrientationChange)
    window.addEventListener('resize', handlePOIOrientationChange)
    
    return () => {
      window.removeEventListener('orientationchange', handlePOIOrientationChange)
      window.removeEventListener('resize', handlePOIOrientationChange)
    }
  }, [currentPOI, hdriBlend])
  
  const isMobile = window.innerWidth <= 768
  const isPortraitMode = portraitMode
  const scene2POIs = useMemo(() => ['POI_BA_ST1', 'POI_BA_ST2', 'POI_BA_ST3', 'POI_BA_PRO'], [])
  
  // Track active animation frames for scene3 transitions
  const activeAnimationFramesRef = useRef(new Set())
  
  const trackAnimationFrame = useCallback((frameId) => {
    activeAnimationFramesRef.current.add(frameId)
    return frameId
  }, [])
  
  const cancelAllAnimationFrames = useCallback(() => {
    activeAnimationFramesRef.current.forEach(frameId => {
      cancelAnimationFrame(frameId)
    })
    activeAnimationFramesRef.current.clear()
  }, [])
  
  // Sync hotspots from window global
  useEffect(() => {
    const interval = setInterval(() => {
      const frameHotspots = window.__hotspotsFrame || []
      if (JSON.stringify(frameHotspots) !== JSON.stringify(lastHotspotsRef.current)) {
        lastHotspotsRef.current = frameHotspots
        setHotspots(frameHotspots)
      }
    }, 16) // ~60fps
    
    return () => clearInterval(interval)
  }, [])
  
  const handleHotspotClick = useCallback((hotspot) => {
    if (hotspot.name === 'POI_ORIGIN' || hotspot.name === 'POI_LP_Origin') {
      window.dispatchEvent(new CustomEvent('trigger-launch', {}))
      return
    }
    
    const isPortrait = window.innerWidth < window.innerHeight
    const isLandscape = !isPortrait
    
    if (hotspot.name.startsWith('POI_LP_')) {
      const cameraConfig = getLaunchPlatformCameraPosition(hotspot.name, isLandscape)
      if (cameraConfig) {
        setCurrentPOI(hotspot.name)
        onPOIStateChange(true, hotspot.name)
        window.dispatchEvent(new CustomEvent('scene-camera-move', {
          detail: {
            targetPosition: cameraConfig.position,
            targetLookAt: cameraConfig.target,
            duration: ANIMATION_TIMINGS.cameraMovement
          }
        }))
      }
    } else if (hotspot.name.startsWith('POI_BA_')) {
      // Special handling for Prospero - trigger Scene 3 transition
      if (hotspot.name === 'POI_BA_PRO') {
        // Cancel any existing scene3 animations
        cancelAllAnimationFrames()
        
        setCurrentPOI(hotspot.name)
        onPOIStateChange(true, hotspot.name)
        
        // Animate scene3Blend from 0 to 1 over 3 seconds
        const startTime = performance.now()
        const duration = 3000
        
        const animateScene3 = (currentTime) => {
          const elapsed = currentTime - startTime
          const progress = Math.min(elapsed / duration, 1)
          
          // Ease in-out cubic
          const eased = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2
          
          setScene3Blend(eased)
          
          if (progress < 1) {
            trackAnimationFrame(requestAnimationFrame(animateScene3))
          }
        }
        
        trackAnimationFrame(requestAnimationFrame(animateScene3))
        
        // Still move camera to Prospero POI
        const isLandscape = window.innerWidth > window.innerHeight
        const cameraConfig = getBlackArrowCameraPosition(hotspot.name, isLandscape)
        if (cameraConfig) {
          window.dispatchEvent(new CustomEvent('scene-camera-move', {
            detail: {
              targetPosition: cameraConfig.position,
              targetLookAt: cameraConfig.target,
              duration: ANIMATION_TIMINGS.cameraMovement
            }
          }))
        }
      } else {
        // Other Stage POIs - normal handling
        const isLandscape = window.innerWidth > window.innerHeight
        const cameraConfig = getBlackArrowCameraPosition(hotspot.name, isLandscape)
        if (cameraConfig) {
          setCurrentPOI(hotspot.name)
          onPOIStateChange(true, hotspot.name)
          window.dispatchEvent(new CustomEvent('scene-camera-move', {
            detail: {
              targetPosition: cameraConfig.position,
              targetLookAt: cameraConfig.target,
              duration: ANIMATION_TIMINGS.cameraMovement
            }
          }))
        }
      }
    }
  }, [onPOIStateChange, scenesRef, setScene3Blend, cancelAllAnimationFrames, trackAnimationFrame])
  
  const handleNavigatePOI = useCallback((direction) => {
    if (!currentPOI) return
    const currentIndex = scene2POIs.indexOf(currentPOI)
    if (currentIndex === -1) return
    
    const nextIndex = direction === 'next' 
      ? (currentIndex + 1) % scene2POIs.length
      : (currentIndex - 1 + scene2POIs.length) % scene2POIs.length
    
    const nextPOI = scene2POIs[nextIndex]
    const isLandscape = window.innerWidth > window.innerHeight
    const cameraConfig = getBlackArrowCameraPosition(nextPOI, isLandscape)
    
    if (cameraConfig) {
      setCurrentPOI(nextPOI)
      onPOIStateChange(true, nextPOI)
      
      // Check if we're navigating to or from Prospero
      const isNavigatingToProspero = nextPOI === 'POI_BA_PRO'
      const isNavigatingFromProspero = currentPOI === 'POI_BA_PRO'
      
      if (isNavigatingToProspero) {
        // Cancel any existing scene3 animations
        cancelAllAnimationFrames()
        
        // Animate scene3Blend from 0 to 1 over 3 seconds
        const startTime = performance.now()
        const duration = 3000
        
        const animateScene3 = (currentTime) => {
          const elapsed = currentTime - startTime
          const progress = Math.min(elapsed / duration, 1)
          
          // Ease in-out cubic
          const eased = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2
          
          setScene3Blend(eased)
          
          if (progress < 1) {
            trackAnimationFrame(requestAnimationFrame(animateScene3))
          }
        }
        
        trackAnimationFrame(requestAnimationFrame(animateScene3))
      } else if (isNavigatingFromProspero) {
        // Cancel any existing scene3 animations
        cancelAllAnimationFrames()
        
        // Animate scene3Blend from 1 to 0 over 2 seconds
        const startTime = performance.now()
        const duration = 2000
        const startBlend = scene3Blend
        
        const animateBack = (currentTime) => {
          const elapsed = currentTime - startTime
          const progress = Math.min(elapsed / duration, 1)
          
          // Ease in-out cubic
          const eased = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2
          
          setScene3Blend(startBlend * (1 - eased))
          
          if (progress < 1) {
            trackAnimationFrame(requestAnimationFrame(animateBack))
          } else {
            setScene3Blend(0)
          }
        }
        
        trackAnimationFrame(requestAnimationFrame(animateBack))
      }
      
      window.dispatchEvent(new CustomEvent('scene-camera-move', {
        detail: {
          targetPosition: cameraConfig.position,
          targetLookAt: cameraConfig.target,
          duration: ANIMATION_TIMINGS.cameraMovement
        }
      }))
    }
  }, [currentPOI, scenesRef, scene2POIs, onPOIStateChange, scene3Blend, setScene3Blend, cancelAllAnimationFrames, trackAnimationFrame])
  
  const handleBackToDefault = useCallback(() => {
    const isScene2 = hdriBlend >= 1
    const isPortrait = window.innerWidth < window.innerHeight
    const isLandscape = !isPortrait
    const isMobile = window.innerWidth <= 767
    const isMobileLandscape = isMobile && isLandscape
    const defaults = getDefaultCameraPosition(isScene2 ? 'scene2' : 'scene1', isLandscape)
    
    // Update state immediately to trigger text box hide animation
    onPOIStateChange(false, null)
    setCurrentPOI(null) // Clear current POI to prevent orientation change from jumping back

    let targetPos = defaults.position.clone()
    let targetLookAt = defaults.target.clone()
    
    // Apply x offset for Scene 2 (portrait and desktop landscape, but not mobile landscape)
    if (isScene2 && !isMobileLandscape) {
      targetLookAt.x = 2
    }
    
    // If in Scene 3, animate back to Scene 2 (regardless of orientation)
    if (scene3Blend > 0) {
      const startTime = performance.now()
      const duration = 2000
      const startBlend = scene3Blend
      
      const animateBack = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // Ease in-out cubic
        const eased = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2
        
        setScene3Blend(startBlend * (1 - eased))
        
        if (progress < 1) {
          requestAnimationFrame(animateBack)
        } else {
          setScene3Blend(0)
        }
      }
      
      requestAnimationFrame(animateBack)
    }
    
    window.dispatchEvent(new CustomEvent('scene-camera-move', {
      detail: {
        targetPosition: targetPos,
        targetLookAt: targetLookAt,
        duration: ANIMATION_TIMINGS.cameraMovement
      }
    }))
  }, [hdriBlend, onPOIStateChange, scene3Blend, setScene3Blend, setCurrentPOI])
  
  const recoverHotspot = useMemo(() => {
    return hotspots.find(h => h.name === 'BTN_BA_RECOVER') || null
  }, [hotspots])
  
  // Pass handleBackToDefault to parent component
  useEffect(() => {
    if (onBackToDefaultReady) {
      onBackToDefaultReady(handleBackToDefault)
    }
  }, [onBackToDefaultReady, handleBackToDefault])
  
  useEffect(() => {
    // Only update if hdriBlend has actually changed enough to matter
    if (lastHdriBlendRef.current === null || Math.abs(hdriBlend - lastHdriBlendRef.current) > 0.01) {
      lastHdriBlendRef.current = hdriBlend
      if (hdriBlend > 0) {
        setScene1LabelOpacity(isMobile ? 0 : (1 - hdriBlend))
      } else {
        // Ensure Scene 1 labels are fully visible when back in Scene 1
        setScene1LabelOpacity(1)
        // Reset Earth environment when returning to Scene 1
        if (earthEnvBlend > 0) {
          setEarthEnvBlend(0)
        }
      }
    } else if (hdriBlend === 0 && scene1LabelOpacity !== 1) {
      // Safety check: if hdriBlend is exactly 0 but opacity isn't 1, fix it
      setScene1LabelOpacity(1)
      // Also reset Earth environment
      if (earthEnvBlend > 0) {
        setEarthEnvBlend(0)
      }
    }
  }, [hdriBlend, isMobile, scene1LabelOpacity, earthEnvBlend, setEarthEnvBlend])
  
  useEffect(() => {
    const handleAnimationProgress = (event) => {
      const { progress, shouldDraw } = event.detail
      if (shouldDraw) {
        const easeOutQuad = isMobile ? 1 : (1 - Math.pow(1 - progress, 2))
        setLabelOpacity(easeOutQuad)
      } else {
        setLabelOpacity(0)
      }
    }
    
    window.addEventListener('dimension-animation-progress', handleAnimationProgress)
    return () => {
      window.removeEventListener('dimension-animation-progress', handleAnimationProgress)
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current)
    }
  }, [isMobile])
  
  useEffect(() => {
    if (hotspots.length === 0) {
      setShowHotspots(false)
      hasShownRef.current = false
    } else if (!hasShownRef.current) {
      hasShownRef.current = true
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current)
      requestAnimationFrame(() => {
        fadeTimeoutRef.current = setTimeout(() => {
          setShowHotspots(true)
        }, 50)
      })
    }
  }, [hotspots])

  return (
    <div className="hotspots-overlay">
      <DimensionLinesCanvas 
        hotspots={hotspots}
        isAtPOI={isAtPOI}
        hdriBlend={hdriBlend}
        scenesRef={scenesRef}
        cameraZoom={cameraZoom}
        cameraRotation={cameraRotation}
        portraitMode={portraitMode}
        isMobile={isMobile}
      />

      {hotspots.length > 0 && !isAtPOI && hotspots.map((hotspot) => {
        const isScene2Label = hotspot.name.startsWith('POI_BA_')
        
        // Detect tablet portrait
        const isTabletPortrait = !isMobile && portraitMode && window.innerWidth > 767 && window.innerWidth <= 1024
        
        // Desktop landscape: position labels at the end of dimension lines (80px down from POI)
        // Mobile/portrait: no horizontal offset needed since dimension lines are hidden
        const labelOffsetY = (!isMobile && !portraitMode && isScene2Label) ? 80 : 0
        const mobileScene2OffsetY = (isScene2Label && isMobile) ? (portraitMode ? -10 : 20) : 0
        
        const leftPosition = `${hotspot.x}px`
        const topPosition = `${hotspot.y + labelOffsetY + mobileScene2OffsetY}px`
        
        // Calculate transform based on device and scene
        let transformValue
        if (isScene2Label && !portraitMode) {
          // Desktop landscape Scene 2: translateX + rotate
          transformValue = `translateX(-50%) rotate(${labelRotations[hotspot.name] || 0}deg)`
        } else if (isScene2Label && isTabletPortrait) {
          // Tablet portrait Scene 2: shift 60px to the RIGHT (no centering) + rotate
          transformValue = `translateX(60px) rotate(${labelRotations[hotspot.name] || 0}deg)`
        } else {
          // Default: just rotate
          transformValue = `rotate(${labelRotations[hotspot.name] || 0}deg)`
        }
        
        return hotspot.isVisible && !hotspot.name.startsWith('BTN_') ? (
          <div
            key={hotspot.name}
            className={`hotspot-label-item ${showHotspots ? 'hotspot-visible' : ''} ${hotspot.name === 'POI_LP_Origin' ? 'black-arrow-origin' : ''} ${hotspot.name === 'POI_BA_PRO' ? 'prospero-origin' : ''}`}
            style={{
              left: leftPosition,
              top: topPosition,
              opacity: isScene2Label ? labelOpacity : scene1LabelOpacity,
              transition: isMobile ? 'none' : 'opacity 0.05s linear',
              transform: transformValue,
              pointerEvents: (!isScene2Label && scene1LabelOpacity < 0.95) ? 'none' : 'auto'
            }}
            onClick={() => handleHotspotClick(hotspot)}
          >
            {getPOILabel(hotspot.name, !portraitMode)}
          </div>
        ) : null
      })}
      
      {isAtPOI && currentPOI && currentPOI.startsWith('POI_BA_') && (
        <>
          <button
            className="DSL_Rectangular_Button nav-button nav-previous"
            onClick={() => handleNavigatePOI('previous')}
            title="Previous Stage"
          >
            &lt;
          </button>
          <button
            className="DSL_Rectangular_Button nav-button nav-next"
            onClick={() => handleNavigatePOI('next')}
            title="Next Stage"
          >
            &gt;
          </button>
        </>
      )}
    </div>
  )
}

// Add more UI elements here as needed
export function Title({ activePOI = null }) {
  const [dotStates, setDotStates] = useState(Array(96).fill(0).map(() => Math.random() > 0.5))
  const { hdriBlend } = useAnimation()
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDotStates(Array(96).fill(0).map(() => Math.random() > 0.5))
    }, 500) // Randomize every 500ms
    
    return () => clearInterval(interval)
  }, [])
  
  // Determine title and subtitle based on active POI and scene
  let mainTitle = 'THE BLACK ARROW PROGRAMME'
  let subTitle = 'DARK STAR LABS PRESENTS'
  
  if (hdriBlend >= 1) {
    // In Scene 2
    if (activePOI === 'POI_BA_ST1') {
      mainTitle = 'FIRST STAGE'
      subTitle = 'BLACK ARROW'
    } else if (activePOI === 'POI_BA_ST2') {
      mainTitle = 'SECOND STAGE'
      subTitle = 'BLACK ARROW'
    } else if (activePOI === 'POI_BA_ST3') {
      mainTitle = 'THIRD STAGE'
      subTitle = 'BLACK ARROW'
    } else if (activePOI === 'POI_BA_PRO') {
      mainTitle = 'PROSPERO'
      subTitle = 'BLACK ARROW'
    } else {
      // Default Scene 2 view (no POI selected)
      mainTitle = 'BLACK ARROW'
      subTitle = 'THE BLACK ARROW PROJECT'
    }
  }
  
  return (
    <div className="title-container">
      <div className="title-subtitle">{subTitle}</div>
      <div className="title-main">{mainTitle}</div>
      <div className="title-row">
        <div className="title-info-box">UK SPACE HISTORY</div>
        <div className="dot-grid">
          <div className="dot-row">
            {dotStates.slice(0, 48).map((isOn, i) => (
              <div key={i} className={`dot ${isOn ? 'dot-on' : 'dot-off'}`}></div>
            ))}
          </div>
          <div className="dot-row">
            {dotStates.slice(48, 84).map((isOn, i) => (
              <div key={i + 48} className={`dot ${isOn ? 'dot-on' : 'dot-off'}`}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// TextBox component - displays content for POI sections

export function TextBox({ isAtPOI, activePOI, hdriBlend, onBackToDefault }) {
  // Show in both Scene 1 (POI_LP_*) and Scene 2 (POI_BA_*) POI sections
  // Also show in Scene 2 default view (not at POI) - but not on mobile
  // Hide all POI boxes in mobile landscape
  const isScene1 = hdriBlend < 1
  const isScene2 = hdriBlend >= 1
  const isMobile = window.innerWidth <= 767
  // Detect tablets by touch capability and screen size (includes iPad Pro)
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  const isTablet = isTouchDevice && window.innerWidth > 767 && window.innerWidth <= 1366
  const isLandscape = window.innerWidth > window.innerHeight
  const isPortrait = window.innerWidth < window.innerHeight
  const isMobileLandscape = isMobile && isLandscape
  const isTabletLandscape = isTablet && isLandscape
  const isTabletPortrait = isTablet && isPortrait
  const isScene2Default = isScene2 && !isAtPOI && !isMobile && !isTabletPortrait
  
  const shouldShow = (isAtPOI && activePOI && !isMobileLandscape && (
    (isScene1 && activePOI.startsWith('POI_LP_')) ||
    (isScene2 && activePOI.startsWith('POI_BA_'))
  )) || isScene2Default

  const [isVisible, setIsVisible] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [contentPOI, setContentPOI] = useState(null)
  const [isScene2Content, setIsScene2Content] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const timeoutRef = useRef(null)
  const prevShouldShowRef = useRef(null)
  const unmountTimeoutRef = useRef(null)
  
  // Check if we're on mobile or tablet in Scene 1 (show expand button)
  // Tablet portrait in Scene 1: show expand button
  // Tablet landscape in Scene 1: show expand button  
  // Scene 2 on tablet (both orientations): NO expand button, work like desktop
  // Exception: Prospero on tablet portrait should be collapsible
  const isProspero = activePOI === 'POI_BA_PRO'
  const shouldShowExpandButton = (isMobile || (isTablet && isScene1) || (isProspero && isTabletPortrait)) && isMounted
  const shouldShowBackButton = isAtPOI && isMounted
  
  // Determine if animations should be used (desktop only, no animations on mobile/tablet)
  const shouldUseAnimations = !isMobile && !isTablet

  // Listen for animation start to immediately hide text box when returning to Scene 1
  useEffect(() => {
    const handleAnimationStart = (event) => {
      // If animation is reversed (going back to Scene 1) and we're showing Scene 2 content
      if (event.detail.isReversed && isScene2Content && isMounted) {
        // Immediately trigger slide out
        setIsVisible(false)
        // Unmount after animation completes
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          setIsMounted(false)
          setContentPOI(null)
          setIsScene2Content(false)
          timeoutRef.current = null
        }, 650)
      }
      // If animation is going to Scene 2 (not reversed), hide text box during animation
      else if (!event.detail.isReversed && isMounted) {
        setIsVisible(false)
      }
    }
    
    window.addEventListener('animation-start', handleAnimationStart)
    return () => {
      window.removeEventListener('animation-start', handleAnimationStart)
    }
  }, [isScene2Content, isMounted])

  useEffect(() => {
    // On first render, initialize the ref
    if (prevShouldShowRef.current === null) {
      prevShouldShowRef.current = shouldShow
      
      // If shouldShow is true on mount, trigger mount immediately
      if (shouldShow) {
        setContentPOI(activePOI)
        setIsScene2Content(isScene2)
        setIsMounted(true)
        
        // Desktop: delay visibility for animation, Mobile/Tablet: immediate
        if (shouldUseAnimations) {
          timeoutRef.current = setTimeout(() => {
            setIsVisible(true)
            timeoutRef.current = null
          }, 50)
        } else {
          setIsVisible(true)
        }
      }
      return
    }
    
    if (shouldShow && !prevShouldShowRef.current) {
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      
      // Mount immediately
      setContentPOI(activePOI)
      setIsScene2Content(isScene2)
      setIsMounted(true)
      
      // Desktop: delay for animation, Mobile/Tablet: immediate
      if (shouldUseAnimations) {
        // For Scene 2 on desktop, delay showing until animation completes (1000ms)
        // For Scene 1, show after small delay
        const delay = isScene2 ? 1000 : 50
        timeoutRef.current = setTimeout(() => {
          setIsVisible(true)
          timeoutRef.current = null
        }, delay)
      } else {
        // Mobile/Tablet: Show immediately, no animation
        setIsVisible(true)
      }
    } else if (shouldShow && prevShouldShowRef.current && activePOI !== contentPOI) {
      // POI changed while text box is visible - update content without animation
      setContentPOI(activePOI)
      setIsExpanded(false) // Reset expanded state when POI changes
    } else if (!shouldShow && prevShouldShowRef.current) {
      // Clear any pending timeouts first
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current)
        unmountTimeoutRef.current = null
      }
      
      setIsVisible(false)
      setIsExpanded(false) // Reset expanded state when closing
      
      // Desktop: wait for slide-out animation, Mobile/Tablet: unmount immediately
      if (shouldUseAnimations) {
        unmountTimeoutRef.current = setTimeout(() => {
          setIsMounted(false)
          setContentPOI(null)
          setIsScene2Content(false)
          unmountTimeoutRef.current = null
        }, 650)
      } else {
        // Mobile/Tablet: Unmount immediately, no animation
        setIsMounted(false)
        setContentPOI(null)
        setIsScene2Content(false)
      }
    }
    
    prevShouldShowRef.current = shouldShow
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current)
      }
    }
  }, [shouldShow, activePOI, hdriBlend, isScene2, shouldUseAnimations])

  if (!isMounted) return null

  // Use the stored isScene2Content state to determine styling
  const isScene2DefaultView = !contentPOI && isScene2Content
  
  // Get content - use default Scene 2 text if in default view
  let content
  if (isScene2DefaultView) {
    content = SCENE2_DEFAULT_CONTENT
  } else if (contentPOI) {
    content = getContentForPOI(contentPOI)
  } else {
    // Fallback to prevent errors during unmounting
    return null
  }

  return (
    <div className={`text-box-container${isScene2Content ? ' text-box-scene2' : ''}${contentPOI === 'POI_BA_PRO' ? ' text-box-prospero' : ''}${contentPOI === 'POI_BA_ST3' ? ' text-box-st3' : ''}${isVisible ? ' text-box-in' : ' text-box-out'}${isExpanded ? ' text-box-expanded' : ''}${shouldShowExpandButton ? ' text-box-collapsible' : ''}`}>
      <div className="text-box-content">
        <h2 className="text-box-title">{content.title}</h2>
        <div className="text-box-body">
          {(contentPOI === 'POI_LP_Shack' || contentPOI === 'POI_BA_PRO' || contentPOI === 'POI_LP_Clamp' || contentPOI === 'POI_LP_Fuel') ? (
            <span dangerouslySetInnerHTML={{ __html: content.body }} />
          ) : (
            <p style={{ whiteSpace: 'pre-line' }}>{content.body}</p>
          )}
        </div>
      </div>
      <div className="text-box-buttons">
        {shouldShowBackButton && (
          <button 
            className="text-box-back-button"
            onClick={onBackToDefault}
          >
            Back
          </button>
        )}
        {shouldShowExpandButton && (
          <button 
            className="text-box-expand-button"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Close' : 'More'}
          </button>
        )}
      </div>
    </div>
  )
}

// Standalone Back Button for mobile landscape Scene 1 and Scene 2
export function StandaloneBackButton({ isAtPOI, hdriBlend, onBackToDefault }) {
  const isMobile = window.innerWidth <= 767
  const isLandscape = window.innerWidth > window.innerHeight
  const isMobileLandscape = isMobile && isLandscape
  
  // Show in mobile landscape when at a POI (both Scene 1 and Scene 2)
  const shouldShow = isAtPOI && isMobileLandscape
  
  if (!shouldShow) return null
  
  return (
    <div className="standalone-back-button-container">
      <div className="rotate-message">Rotate to portrait to learn more</div>
      <div className="spacer">
        <button 
          className="standalone-back-button"
          onClick={onBackToDefault}
        >
          Back
        </button>
      </div>
    </div>
  )
}

// Standalone Back Button for tablet landscape and portrait Scene 2
export function Scene2BackButtonTablet({ isAtPOI, hdriBlend, onBackToDefault, activePOI }) {
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  const isTablet = isTouchDevice && window.innerWidth > 767 && window.innerWidth <= 1366
  const isScene2 = hdriBlend >= 1
  
  // Show in tablet (both landscape and portrait) Scene 2 when at a POI (viewing a rocket part)
  // activePOI must start with POI_BA_ (rocket parts)
  const shouldShow = isAtPOI && isTablet && isScene2 && activePOI && activePOI.startsWith('POI_BA_')
  
  if (!shouldShow) return null
  
  return (
    <button 
      className="scene2-back-button-tablet"
      onClick={onBackToDefault}
    >
      BACK
    </button>
  )
}

// Standalone Back Button for desktop Scene 2
export function Scene2BackButtonDesktop({ isAtPOI, hdriBlend, onBackToDefault, activePOI }) {
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  const isTablet = isTouchDevice && window.innerWidth > 767 && window.innerWidth <= 1366
  const isDesktop = !isTouchDevice || window.innerWidth > 1366
  const isScene2 = hdriBlend >= 1
  
  // Show on desktop Scene 2 when at a POI (viewing a rocket part)
  // Don't show on tablet (tablet has its own button)
  // activePOI must start with POI_BA_ (rocket parts)
  const shouldShow = isAtPOI && isDesktop && !isTablet && isScene2 && activePOI && activePOI.startsWith('POI_BA_')
  
  if (!shouldShow) return null
  
  return (
    <button 
      className="scene2-back-button-desktop"
      onClick={onBackToDefault}
    >
      BACK
    </button>
  )
}

// Logo Group - Bottom left on desktop and tablet
export function LogoGroup({ isAtPOI, hdriBlend }) {
  const isDesktop = window.innerWidth > 1366
  const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1366
  const isMobilePortrait = window.innerWidth <= 767 && window.innerHeight > window.innerWidth
  const isScene2 = hdriBlend > 0.5
  
  if (!isDesktop && !isTablet && !isMobilePortrait) return null
  if (isAtPOI && (isTablet || isMobilePortrait)) return null // Hide on tablet/mobile when POI selected, but not on desktop
  
  // Calculate opacity based on hdriBlend (fade out as we approach Scene 2)
  const opacity = isScene2 ? 0 : 1 - (hdriBlend * 2) // Fade out in first half of transition
  
  // Check if SpaceComm mode is active (disable link if /?sc=true)
  const isSpaceCommMode = new URLSearchParams(window.location.search).get('sc') === 'true'
  
  return (
    <div className="logo-group" style={{ opacity, transition: 'opacity 0.6s ease' }}>
      <img 
        src="/textures/logos/DSL Logo Stacked Light and Orange.svg" 
        alt="Dark Star Labs" 
        className="logo-image logo-dsl" 
        onClick={isSpaceCommMode ? undefined : () => window.open('https://darkstarlabs.io', '_blank')}
        style={{ cursor: isSpaceCommMode ? 'default' : 'pointer', pointerEvents: 'auto' }}
      />
      <img src="/textures/logos/RAE_Logo.webp" alt="RAE" className="logo-image logo-rae" />
      <img src="/textures/logos/SaundersRoe_Logo.webp" alt="Saunders-Roe" className="logo-image logo-saunders" />
      <img src="/textures/logos/WestlandAircraft_Logo.webp" alt="Westland Aircraft" className="logo-image logo-westland" />
    </div>
  )
}

// RAAF Woomera Badge - Bottom right on desktop only
export function WoomeraBadge({ isAtPOI, hdriBlend }) {
  const isDesktop = window.innerWidth > 1366
  const isScene2 = hdriBlend > 0.5
  
  if (!isDesktop) return null
  
  // Offset values (x, y) to position the dot relative to center
  const dotOffsetX = 18 // pixels from center
  const dotOffsetY = 20 // pixels from center
  
  // Calculate opacity: fade out when at POI or transitioning to Scene 2
  const opacity = (isAtPOI || isScene2) ? 0 : 1 - (hdriBlend * 2) // Fade out in first half of transition
  
  return (
    <div className="woomera-badge" style={{ opacity, transition: 'opacity 0.6s ease' }}>
      <img src="/textures/logos/RAAF-Woomera.webp" alt="RAAF Woomera" className="woomera-image" />
      <div 
        className="woomera-dot" 
        style={{ 
          transform: `translate(${dotOffsetX}px, ${dotOffsetY}px)` 
        }}
      >
        <div className="woomera-dot-label">RAAF-WOOMERA</div>
      </div>
    </div>
  )
}

// Stage Specifications Grid - Top right on desktop for Scene 2 stages
export function StageSpecs({ activePOI, isAtPOI }) {
  const isDesktop = window.innerWidth > 1366
  
  if (!isDesktop) return null
  if (!isAtPOI) return null
  
  // Stage specifications data
  const stageData = {
    'POI_BA_ST1': {
      title: 'First stage',
      specs: [
        { label: 'Powered by', value: 'Gamma 8', link: true },
        { label: 'Maximum thrust', value: '256.4 kilonewtons (57,600 lbf)' },
        { label: 'Specific impulse', value: '265 seconds (2.60 km/s)', link: true },
        { label: 'Burn time', value: '131 seconds' },
        { label: 'Propellant', value: 'RP-1/HTP', link: true }
      ]
    },
    'POI_BA_ST2': {
      title: 'Second stage',
      specs: [
        { label: 'Powered by', value: 'Gamma 2', link: true },
        { label: 'Maximum thrust', value: '68.2 kilonewtons (15,300 lbf)' },
        { label: 'Specific impulse', value: '265 seconds (2.60 km/s)', link: true },
        { label: 'Burn time', value: '115 seconds' },
        { label: 'Propellant', value: 'RP-1/HTP', link: true }
      ]
    },
    'POI_BA_ST3': {
      title: 'Third stage',
      specs: [
        { label: 'Powered by', value: 'Waxwing', link: true },
        { label: 'Maximum thrust', value: '27.3 kilonewtons (6,100 lbf)' },
        { label: 'Specific impulse', value: '278 seconds (2.73 km/s)', link: true },
        { label: 'Burn time', value: '55 seconds' },
        { label: 'Propellant', value: 'Solid fuel', link: false }
      ]
    }
  }
  
  const currentStage = stageData[activePOI]
  if (!currentStage) return null
  
  return (
    <div className="stage-specs">
      <div className="stage-specs-grid">
        {currentStage.specs.map((spec, index) => (
          <div key={index} style={{ display: 'contents' }}>
            <div className="stage-specs-label">{spec.label}</div>
            <div className={`stage-specs-value ${spec.link ? 'stage-specs-link' : ''}`}>
              {spec.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Add more UI elements here as needed




